<template>
  <!-- 🔥 整个页面加 loading -->
  <div
    class="image-library-container"
    v-loading="loading"
    element-loading-text="加载中..."
  >
    <!-- ====================== PC 端（原样不动） ====================== -->
    <div class="pc-version">
      <!-- 顶部筛选 + 上传区域 -->
      <div class="search-bar">
        <el-form :inline="true" :model="searchParams" class="query-form">
          <el-form-item label="图片类型">
            <el-select
              v-model="searchParams.type"
              placeholder="请选择图片类型"
              clearable
              style="width: 200px"
            >
              <el-option label="全部图片" value="" />
              <el-option
                v-for="item in image_type"
                :key="item.type"
                :label="item.desc"
                :value="item.type"
              />
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="getImageList">查询</el-button>
            <el-button @click="resetQuery">重置</el-button>
          </el-form-item>
        </el-form>

        <!-- 上传按钮 -->
        <div class="upload-group">
          <el-upload
            class="upload-demo"
            :http-request="uploadAction"
            :file-list="fileList"
            :before-upload="beforeUpload"
            action="javascript:;"
            drag
            multiple
            accept="image/png,image/jpeg,image/jpg,image/gif"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">
              将文件拖到此处，或<em>点击上传</em>
            </div>
            <div class="el-upload__tip" slot="tip">
              只能上传 jpg/png/gif 文件，不超过 5MB
            </div>
          </el-upload>

          <!-- 上传类型 动态渲染 -->
          <el-select
            v-model="uploadType"
            placeholder="上传图片类型"
            size="small"
            style="width: 180px; margin-top: 10px"
          >
            <el-option
              v-for="item in image_type"
              :key="item.type"
              :label="item.desc"
              :value="item.type"
            />
          </el-select>

          <!-- 🔥 新增：文件夹名称输入框 -->
          <el-input
            v-model="levelName"
            placeholder="请输入文件夹名称"
            size="small"
            style="width: 180px; margin-top: 10px"
            clearable
          />
        </div>
      </div>

      <!-- 图片画廊 -->
      <div class="image-grid">
        <div class="image-card" v-for="item in imageList" :key="item.id">
          <img :src="item.url" alt="图库" @click="previewImage(item.url)" />
          <div class="image-info">
            <span class="type-tag" :class="getTypeClass(item.type)">
              {{ getTypeName(item.type) }}
            </span>
          </div>
        </div>
      </div>

      <!-- 空数据提示 -->
      <div v-if="imageList.length === 0 && !loading" class="empty-tip">
        暂无图片数据
      </div>

      <!-- 分页 -->
      <el-pagination
        v-if="total > 0"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="searchParams.page"
        :page-sizes="[10, 20, 50, 100, 200, 500, 800, 1000, 2000]"
        :page-size="searchParams.page_size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        style="text-align: center; margin-top: 20px"
      />
    </div>

    <!-- ====================== 移动端（全新重写） ====================== -->
    <div class="mobile-version">
      <div class="mobile-image-container">
        <!-- 搜索筛选 -->
        <div class="mobile-search-group">
          <el-select
            v-model="searchParams.type"
            placeholder="图片类型"
            clearable
            size="medium"
            style="width: 100%"
          >
            <el-option label="全部图片" value="" />
            <el-option
              v-for="item in image_type"
              :key="item.type"
              :label="item.desc"
              :value="item.type"
            />
          </el-select>

          <div style="display: flex; gap: 8px; margin-top: 8px">
            <el-button
              type="primary"
              plain
              round
              @click="getImageList"
              style="flex: 1"
              >查询</el-button
            >
            <el-button plain round @click="resetQuery" style="flex: 1"
              >重置</el-button
            >
          </div>
        </div>

        <!-- 移动端上传 -->
        <div class="mobile-upload-box">
          <el-upload
            class="mobile-upload"
            :http-request="uploadAction"
            :file-list="fileList"
            :before-upload="beforeUpload"
            action="javascript:;"
            drag
            multiple
            accept="image/png,image/jpeg,image/jpg,image/gif"
          >
            <i class="el-icon-upload"></i>
            <div class="text">点击或拖拽上传图片</div>
          </el-upload>

          <el-select
            v-model="uploadType"
            placeholder="上传图片类型"
            size="medium"
            style="width: 100%; margin-top: 10px"
          >
            <el-option
              v-for="item in image_type"
              :key="item.type"
              :label="item.desc"
              :value="item.type"
            />
          </el-select>

          <!-- 🔥 移动端新增：文件夹名称 -->
          <el-input
            v-model="levelName"
            placeholder="请输入文件夹名称"
            size="medium"
            style="width: 100%; margin-top: 10px"
            clearable
          />
        </div>

        <!-- 移动端图片列表 -->
        <div class="mobile-image-list">
          <div
            class="mobile-image-card"
            v-for="item in imageList"
            :key="item.id"
            @click="previewImage(item.url)"
          >
            <img :src="item.url" />
            <div class="tag" :class="getTypeClass(item.type)">
              {{ getTypeName(item.type) }}
            </div>
          </div>
        </div>

        <!-- 空数据 -->
        <div v-if="imageList.length === 0 && !loading" class="mobile-empty">
          暂无图片
        </div>

        <!-- 分页 -->
        <div style="text-align: center; margin-top: 15px">
          <el-pagination
            v-if="total > 0"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="searchParams.page"
            :page-size="searchParams.page_size"
            layout="total, prev, pager, next, jumper"
            :total="total"
            pager-count="2"
            small
          />
        </div>
      </div>
    </div>

    <!-- 图片预览（共用） -->
    <el-dialog
      title="图片预览"
      :visible.sync="previewVisible"
      width="40%"
      center
      class="preview-dialog1"
      append-to-body
    >
      <img :src="previewUrl" alt="预览" style="width: 100%" />
    </el-dialog>
  </div>
</template>

<script>
import api from "@/api/api.js";

export default {
  name: "ImageLibrary",
  data() {
    return {
      loading: false,
      searchParams: {
        type: "",
        page: 1,
        page_size: 10,
      },
      uploadType: "",
      levelName: "", // 🔥 新增：文件夹名称
      fileList: [],
      imageList: [],
      image_type: [],
      total: 0,
      previewVisible: false,
      previewUrl: "",
    };
  },
  created() {
    this.getImageList();
  },
  methods: {
    // 🔥 自定义上传：调用封装好的 api.images.upload
    async uploadAction({ file }) {
      try {
        // 🔥 校验：必须填写文件夹名称
        // if (!this.levelName) {
        //   this.$message.warning("请输入文件夹名称！");
        //   return;
        // }

        // 构造 formData
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", this.uploadType);
        formData.append("level_name", this.levelName); // 🔥 新增字段

        // 调用封装接口
        const res = await api.images.upload(formData);

        if (res.data.code == 1000) {
          this.fileList = [];
          this.getImageList();
        } else {
        }
      } catch (error) {
        console.error(error);
      }
    },

    // 获取图片列表
    getImageList() {
      this.loading = true;
      api.images
        .index({ params: this.searchParams })
        .then((res) => {
          if (res.data.code == 1000) {
            this.imageList = res.data.data.data || [];
            this.total = res.data.data.total || 0;
            this.image_type = res.data.data.image_type || [];

            // 自动设置第一个类型为默认上传类型
            if (this.image_type.length > 0) {
              this.uploadType = this.image_type[0].type;
            }
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // 重置
    resetQuery() {
      this.searchParams = {
        type: "",
        page: 1,
        page_size: 12,
      };
      this.getImageList();
    },

    // 分页
    handleSizeChange(val) {
      this.searchParams.page_size = val;
      this.getImageList();
    },
    handleCurrentChange(val) {
      this.searchParams.page = val;
      this.getImageList();
    },

    // 上传前校验
    beforeUpload(file) {
      const isImage = file.type.startsWith("image/");
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isImage) {
        this.$message.error("只能上传图片！");
      }
      if (!isLt5M) {
        this.$message.error("图片大小不能超过 5MB!");
      }
      return isImage && isLt5M;
    },

    // 预览
    previewImage(url) {
      this.previewUrl = url;
      this.previewVisible = true;
    },

    // 获取类型名称
    getTypeName(type) {
      const item = this.image_type.find((i) => i.type == type);
      return item ? item.desc : "未知";
    },

    // 获取类型样式
    getTypeClass(type) {
      const clsMap = {
        1: "tag-goods",
        2: "tag-box",
        3: "tag-banner",
        4: "tag-activity",
      };
      return clsMap[type] || "";
    },
  },
};
</script>

<style scoped>
/* ====================== PC 原有样式（样式变量适配，结构不动） ====================== */
.image-library-container {
  padding: 24px;
  background: var(--bg-page);
  min-height: calc(100vh - 80px);
  transition: background-color 0.3s;
}
.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 24px;
}
.query-form {
  display: flex;
  align-items: center;
}
.upload-group {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 上传区域优化 */
::v-deep .upload-demo .el-upload-dragger {
  width: 280px;
  height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: var(--bg-card);
  border-color: var(--border-base);
}
::v-deep .upload-demo .el-upload__text {
  font-size: 12px;
  color: var(--text-regular);
}
::v-deep .upload-demo .el-upload__tip {
  font-size: 11px;
  color: var(--text-secondary);
}
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 16px;
}
.image-card {
  border-radius: 10px;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--border-base);
  transition: all 0.25s ease;
}
.image-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  border-color: var(--el-color-primary);
}
.image-card img {
  width: 100%;
  height: 140px;
  object-fit: cover;
  cursor: pointer;
}
.image-info {
  padding: 8px 10px;
  text-align: center;
  background: var(--bg-card);
}
.type-tag {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #fff;
}
.tag-goods {
  background: #409eff;
}
.tag-box {
  background: #67c23a;
}
.tag-banner {
  background: #e6a23c;
}
.tag-activity {
  background: #f56c6c;
}
.empty-tip {
  text-align: center;
  padding: 100px 0;
  color: var(--text-secondary);
  font-size: 14px;
  background: var(--bg-card);
  border-radius: 10px;
  border: 1px solid var(--border-base);
}

/* ====================== 移动端全新样式（变量适配） ====================== */
.mobile-image-container {
  padding: 12px;
  box-sizing: border-box;
  background: var(--bg-page);
}
.mobile-search-group {
  margin-bottom: 15px;
}
.mobile-upload-box {
  margin-bottom: 18px;
}
::v-deep .mobile-upload .el-upload-dragger {
  width: 100% !important;
  height: 100px !important;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: var(--bg-card);
  border-color: var(--border-base);
}
.mobile-upload .text {
  font-size: 13px;
  color: var(--text-regular);
  margin-top: 4px;
}

.mobile-image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.mobile-image-card {
  width: calc(50% - 5px);
  background: var(--bg-card);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--border-base);
}
.mobile-image-card img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
}
.mobile-image-card .tag {
  text-align: center;
  font-size: 12px;
  color: #fff;
  padding: 3px 0;
}
.mobile-empty {
  width: 100%;
  padding: 60px 0;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
  background: var(--bg-card);
  border-radius: 10px;
  border: 1px solid var(--border-base);
}
</style>


// WEBPACK FOOTER //
// src/pages/content/image.vue